import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Card, Alert, Statistic, Row, Col, Badge, Spin, Table, Progress } from 'antd';
import { RobotOutlined, RiseOutlined, BulbOutlined, AlertOutlined, DownOutlined, ThunderboltOutlined, HistoryOutlined } from '@ant-design/icons';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, Filler } from 'chart.js';
import { useFeatures } from '../context/FeatureContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';
import './AIInsights.css';

// Registrar plugin Filler
ChartJS.register(Filler);

interface AIInsight {
  type: 'success' | 'warning' | 'error' | 'info';
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  details?: any[];
}

interface PredictionData {
  predictions: number[];
  trend: 'increasing' | 'decreasing' | 'stable';
  confidence: number;
  algorithm?: string;
}

interface ChartContext {
  data: any;
  historicalPoints: number;
  positiveHistoricalPoints: number;
  hasPredictedValues: boolean;
}

interface LearningStatus {
  tenantId: string;
  subscriptionId: string;
  lastCheckedAt: string;
  modelInfo?: {
    current_model?: 'global' | 'personalized';
    days_of_data?: number;
    total_data_points?: number;
    can_upgrade?: boolean;
    days_until_upgrade?: number;
    personal_model_accuracy?: number | null;
    global_model_accuracy?: number | null;
    recommendation?: string;
  };
  learningStats?: {
    total_predictions?: number;
    current_accuracy?: number;
    initial_accuracy?: number;
    improvement?: number;
    last_training?: string | null;
    user_feedback_count?: number;
    helpful_recommendations?: number;
  };
  pendingStats?: {
    pending_predictions?: number;
    next_validation_at?: string | null;
    next_target_period?: string | null;
  };
}

interface AIInsightsProps {
  subscriptionData?: any;
}

const providerLabel = (provider?: string) => {
  if (provider === 'aws') return 'AWS';
  if (provider === 'gcp') return 'GCP';
  return 'Azure';
};

const normalizeInsightType = (type?: string): AIInsight['type'] => {
  if (type === 'success' || type === 'warning' || type === 'error' || type === 'info') return type;
  if (type === 'anomaly') return 'warning';
  if (type === 'optimization') return 'success';
  return 'info';
};

const normalizeTrend = (trend?: string): PredictionData['trend'] => {
  if (trend === 'decreasing' || trend === 'stable') return trend;
  return 'increasing';
};

const normalizePredictionData = (source: any): PredictionData | null => {
  if (!source) return null;

  const predictionValues = Array.isArray(source.predictions)
    ? source.predictions.map(Number).filter((value: number) => Number.isFinite(value))
    : [];

  if (!predictionValues.length) return null;

  return {
    predictions: predictionValues,
    trend: normalizeTrend(source.trend),
    confidence: Number(source.confidence || 0.7),
    algorithm: source.algorithm || source.model || 'IA'
  };
};

const normalizeInsightPayload = (insights: any[] = []): AIInsight[] =>
  insights.map((insight: any) => ({
    type: normalizeInsightType(insight.type),
    title: insight.title || insight.name || 'Insight de IA',
    description: insight.description || insight.summary || 'Análise gerada pela IA para o ambiente conectado.',
    priority: insight.priority || 'medium',
    details: insight.details || insight.data || []
  }));

const buildEmbeddedSecurityInsight = (subscriptionData: any): AIInsight[] => {
  const securityRiskAnalysis = subscriptionData?.securityRiskAnalysis;
  if (securityRiskAnalysis) {
    const topRisks = Array.isArray(securityRiskAnalysis.topRisks) ? securityRiskAnalysis.topRisks : [];
    return [{
      type: topRisks.length ? 'warning' : 'success',
      title: `Análise de Segurança ${providerLabel(subscriptionData?.provider)} (IA)`,
      description: securityRiskAnalysis.executiveSummary ||
        (topRisks.length
          ? `${topRisks.length} risco(s) priorizado(s) pela IA para revisão.`
          : 'Nenhum risco relevante encontrado pela IA no snapshot atual.'),
      priority: securityRiskAnalysis.riskScore >= 70 ? 'high' : securityRiskAnalysis.riskScore >= 35 ? 'medium' : 'low',
      details: topRisks.map((risk: any) => ({
        risco: risk.title,
        prioridade: risk.priority,
        score: risk.score,
        recursosAfetados: risk.affectedCount,
        causaTecnica: risk.technicalCause,
        impacto: risk.businessImpact,
        proximaAcao: risk.recommendedAction
      }))
    }];
  }

  const securityDetails = Array.isArray(subscriptionData?.securityDetails) ? subscriptionData.securityDetails : [];
  if (!securityDetails.length) return [];

  return [{
    type: 'warning',
    title: `Análise de Segurança ${providerLabel(subscriptionData?.provider)} (IA)`,
    description: `${securityDetails.length} ponto(s) de segurança encontrados no snapshot atual.`,
    priority: securityDetails.some((item: any) => item.severity === 'Alta') ? 'high' : 'medium',
    details: securityDetails.map((item: any) => ({
      risco: item.title || item.name,
      severidade: item.severity,
      recursosAfetados: item.affected,
      explicacao: item.description,
      proximaAcao: item.recommendation
    }))
  }];
};

const buildEmbeddedOptimizationInsight = (subscriptionData: any): AIInsight[] => {
  const xknow = subscriptionData?.xknowOptimizationAnalysis;
  const xknowOpportunities = Array.isArray(xknow?.opportunities) ? xknow.opportunities : [];
  const xknowMonthly = Number(xknow?.summary?.probable_monthly_savings || 0);
  if (xknow && (xknowOpportunities.length || xknowMonthly > 0)) {
    return [{
      type: 'success',
      title: `Otimização Independente XKnow AI — ${providerLabel(subscriptionData?.provider)}`,
      description: [
        `Potencial provável de ${xknowMonthly.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}/mês.`,
        `${Number(xknow?.summary?.overlap_count || 0)} oportunidade(s) também aparecem nas recomendações do provedor e não devem ser somadas novamente.`,
        `Análise de ${Number(xknow?.summary?.resources_analyzed || 0)} recurso(s), separada da IA nativa da cloud.`
      ].join(' '),
      priority: xknowMonthly >= 10000 ? 'high' : xknowMonthly > 0 ? 'medium' : 'low',
      details: xknowOpportunities.map((item: any) => ({
        recurso: item.resource_name || item.resource_id || 'Análise por serviço',
        oportunidade: item.title,
        evidencias: Array.isArray(item.evidence) ? item.evidence.join(' • ') : 'Evidência não informada',
        confianca: `${Math.round(Number(item.confidence || 0) * 100)}%`,
        economiaProvavel: Number(item.probable_monthly_savings || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        sobreposicaoComCloud: item.overlaps_provider ? 'Sim — não somar novamente' : 'Não identificada',
        proximaAcao: item.next_action
      }))
    }];
  }

  if (subscriptionData?.provider !== 'gcp') return [];
  const strategic = subscriptionData?.strategicSavingsPotential;
  const strategicMonthly = Number(
    strategic?.estimatedMonthlySavings ||
    subscriptionData?.overview?.strategicAnnualSavingsEstimate / 12 ||
    0
  );
  const officialMonthly = Number(
    subscriptionData?.optimizationOpportunities?.estimatedMonthlySavings ||
    subscriptionData?.executiveSummary?.netMonthlySavings ||
    0
  );
  const opportunities = Array.isArray(strategic?.opportunities) ? strategic.opportunities : [];
  if (strategicMonthly <= 0 && officialMonthly <= 0) return [];

  const details = opportunities.length
    ? opportunities.map((item: any) => ({
      alavanca: `${item.service}: ${item.lever}`,
      baseMensal: Number(item.baseMonthlyCost || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      premissa: `${(Number(item.rate || 0) * 100).toFixed(0)}%`,
      economiaMensal: Number(item.estimatedMonthlySavings || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      proximaAcao: 'Validar a premissa com utilização, SKUs e regras comerciais antes da execução.'
    }))
    : [{
      status: 'Potencial estratégico calculado',
      explicacao: `O cenário financeiro estima ${strategicMonthly.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} por mês.`,
      proximaAcao: 'Atualizar o relatório para persistir a composição detalhada por alavanca.'
    }];

  return [{
    type: 'success',
    title: 'Oportunidades de Otimização FinOps',
    description: [
      `Potencial estratégico de ${strategicMonthly.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}/mês.`,
      `Economia oficial identificada pelo Google: ${officialMonthly.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}/mês.`,
      'Os valores são apresentados separadamente porque podem se sobrepor.'
    ].join(' '),
    priority: strategicMonthly >= 10000 ? 'high' : strategicMonthly > 0 ? 'medium' : 'low',
    details
  }];
};

const extractEmbeddedAIData = (subscriptionData: any) => {
  const source = subscriptionData?.aiInsights || subscriptionData?.ai || {};
  const sourceInsights = Array.isArray(source.insights)
    ? normalizeInsightPayload(source.insights)
    : Array.isArray(subscriptionData?.insights)
      ? normalizeInsightPayload(subscriptionData.insights)
      : [];
  const securityInsights = buildEmbeddedSecurityInsight(subscriptionData);
  const optimizationInsights = buildEmbeddedOptimizationInsight(subscriptionData);

  return {
    insights: [...sourceInsights, ...optimizationInsights, ...securityInsights],
    predictions: normalizePredictionData(source.predictions || subscriptionData?.predictions)
  };
};

const hasInsight = (insights: AIInsight[], patterns: RegExp[]) =>
  insights.some(insight => patterns.some(pattern => pattern.test(insight.title)));

const ensureBaselineInsights = (
  sourceInsights: AIInsight[],
  options: {
    provider?: string;
    aiPredictionEnabled: boolean;
    anomalyDetectionEnabled: boolean;
    rightsizingEnabled: boolean;
    hasPredictions: boolean;
  }
) => {
  const nextInsights = [...sourceInsights];
  const provider = providerLabel(options.provider);

  if (options.aiPredictionEnabled && !hasInsight(nextInsights, [/Predi[cç][aã]o/i, /Proje[cç][aã]o/i])) {
    nextInsights.push({
      type: options.hasPredictions ? 'info' : 'warning',
      title: `Predição de Custos ${provider} (IA)`,
      description: options.hasPredictions
        ? 'A IA gerou uma projeção para o período analisado.'
        : 'Aguardando histórico real suficiente para gerar uma projeção confiável.',
      priority: options.hasPredictions ? 'medium' : 'low',
      details: [{
        status: options.hasPredictions ? 'Projeção disponível' : 'Aguardando histórico',
        explicacao: options.hasPredictions
          ? 'Consulte o gráfico de projeção para avaliar tendência e confiança.'
          : 'O modelo evita criar previsão artificial quando ainda não há base histórica positiva suficiente.',
        proximaAcao: 'Manter coletas recorrentes para aumentar a qualidade da previsão.'
      }]
    });
  }

  if (options.anomalyDetectionEnabled && !hasInsight(nextInsights, [/Anomalias/i, /Detectadas/i])) {
    nextInsights.push({
      type: 'success',
      title: 'Anomalias Detectadas',
      description: 'Nenhuma anomalia relevante encontrada pela IA no histórico disponível.',
      priority: 'low',
      details: [{
        status: 'Sem anomalias relevantes',
        explicacao: 'Os custos analisados não ficaram fora do padrão histórico com confiança suficiente.',
        proximaAcao: 'Continuar monitorando novos fechamentos de custo.'
      }]
    });
  }

  if (options.rightsizingEnabled && !hasInsight(nextInsights, [/Oportunidades/i, /Otimiza[cç][aã]o/i])) {
    nextInsights.push({
      type: 'info',
      title: 'Oportunidades de Otimização',
      description: 'Nenhuma oportunidade crítica de otimização foi encontrada nesta análise.',
      priority: 'low',
      details: [{
        status: 'Sem ação crítica',
        explicacao: 'A IA não identificou recomendação com evidência suficiente para priorização imediata.',
        proximaAcao: 'Revisar novamente após novas coletas ou mudanças relevantes no ambiente.'
      }]
    });
  }

  if (!hasInsight(nextInsights, [/Causa Raiz/i, /Varia[cç][aã]o de Custo/i])) {
    nextInsights.push({
      type: 'info',
      title: 'Causa Raiz da Variação de Custo',
      description: 'Sem variação relevante ou dados suficientes para apontar um driver principal nesta consulta.',
      priority: 'low',
      details: [{
        status: 'Sem driver dominante',
        explicacao: 'A IA precisa de variação material entre períodos e detalhamento por serviço para apontar causa raiz.',
        proximaAcao: 'Acompanhar o próximo fechamento mensal e manter o detalhamento de custos por serviço ativo.'
      }]
    });
  }

  return nextInsights;
};

const AIInsights: React.FC<AIInsightsProps> = ({ subscriptionData }) => {
  const { features, isPremium } = useFeatures();
  const { isDark } = useTheme();
  const { user } = useAuth();
  // Diagnostico interno do pipeline de ML (modelo em uso, previsoes validadas,
  // status de coleta de baseline) - nao e informacao de FinOps pro cliente,
  // e so faz sentido pra quem opera a plataforma.
  const canSeeAiLearningDiagnostics = Boolean(
    user?.roles?.some(role => ['admin', 'commercial', 'platform-admin'].includes(role.toLowerCase()))
  );
  const aiPredictionEnabled = isPremium ? features.aiPrediction : true;
  const anomalyDetectionEnabled = isPremium ? features.anomalyDetection : true;
  const rightsizingEnabled = isPremium ? features.intelligentRightsizing : true;
  const [insights, setInsights] = useState<AIInsight[]>([]);
  const [predictions, setPredictions] = useState<PredictionData | null>(null);
  const [loading, setLoading] = useState(false);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState<'idle' | 'loading' | 'ready' | 'unavailable'>('idle');
  const [learningStatus, setLearningStatus] = useState<LearningStatus | null>(null);
  const [learningLoading, setLearningLoading] = useState(false);
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const lastRequestRef = useRef<{ key: string; timestamp: number } | null>(null);
  const awsInsightsCacheRef = useRef<Record<string, { insights: AIInsight[]; predictions: PredictionData | null }>>({});

  const requestKey = useMemo(() => {
    if (!subscriptionData) return '';

    const provider = subscriptionData?.provider || 'azure';
    const subscriptionId =
      subscriptionData?.account?.id ||
      subscriptionData?.scope?.id ||
      subscriptionData?.projectId ||
      subscriptionData?.subscription?.id ||
      subscriptionData?.subscriptionId ||
      localStorage.getItem('selectedSubscription') ||
      'no-subscription';
    const monthlyAverage = subscriptionData?.overview?.monthlyAverageCost || 0;
    const savings = subscriptionData?.executiveSummary?.netMonthlySavings || 0;

    return JSON.stringify({
      provider,
      subscriptionId,
      monthlyAverage,
      savings
    });
  }, [subscriptionData]);

  const subscriptionIdentifier = useMemo(() => {
    if (!subscriptionData) return '';

    if (subscriptionData?.provider === 'aws') {
      return (
        subscriptionData?.account?.id ||
        subscriptionData?.scope?.id ||
        subscriptionData?.subscriptionId ||
        'aws-account'
      );
    }

    if (subscriptionData?.provider === 'gcp') {
      return (
        subscriptionData?.account?.id ||
        subscriptionData?.scope?.id ||
        subscriptionData?.projectId ||
        'gcp-project'
      );
    }

    return (
      subscriptionData?.subscription?.id ||
      subscriptionData?.subscriptionId ||
      subscriptionData?.overview?.subscriptionId ||
      localStorage.getItem('selectedSubscription') ||
      ''
    );
  }, [subscriptionData]);

  const loadLearningStatus = useCallback(async () => {
    if (!subscriptionIdentifier) {
      setLearningStatus(null);
      return;
    }

    setLearningLoading(true);

    try {
      const provider = subscriptionData?.provider || 'azure';
      const response = await apiClient.get(`/ai/learning-status/${encodeURIComponent(subscriptionIdentifier)}`, {
        params: { provider }
      });
      setLearningStatus(response.data);
    } catch (error) {
      console.error('Erro ao carregar status de aprendizado da IA:', error);
      setLearningStatus(null);
    } finally {
      setLearningLoading(false);
    }
  }, [subscriptionIdentifier, subscriptionData?.provider]);

  const loadAIInsights = useCallback(async () => {
    setLoading(true);
    setAnalysisStatus('loading');
    
    try {
      const provider = subscriptionData?.provider || 'azure';
      const isAWS = provider === 'aws';
      const isAzure = provider === 'azure';
      
      if (isAWS) {
        const cachedInsights = awsInsightsCacheRef.current[requestKey];
          if (cachedInsights) {
            setInsights(cachedInsights.insights);
            setPredictions(cachedInsights.predictions);
            setAnalysisStatus(cachedInsights.predictions ? 'ready' : 'unavailable');
            setLoading(false);
            return;
          }
        
        try {
          const accountId =
            subscriptionData?.account?.id ||
            subscriptionData?.scope?.id ||
            'aws-account';

          const response = await apiClient.post(`/cloud/aws/insights/${accountId}`, subscriptionData);
          
          const aiData = response.data;

          if (response.status === 429) {
            console.warn('⏳ AWS insights rate-limited; evitando novas chamadas imediatas');
            throw new Error('Erro na API: 429');
          }

          // Processar insights da IA
          const processedInsights = aiData.insights?.map((insight: any) => ({
            type: insight.type === 'prediction' ? 'info' : 
                  insight.type === 'anomaly' ? 'warning' : 
                  insight.type === 'optimization' ? 'success' : 'info',
            title: insight.title,
            description: insight.description,
            priority: insight.priority || 'medium',
            details: insight.details || insight.data || []
          })) || [];
          
          // Processar predições
          let nextPredictions: PredictionData | null = null;
          if (aiData.predictions) {
            nextPredictions = {
              predictions: aiData.predictions.predictions || [],
              trend: aiData.predictions.trend || 'increasing',
              confidence: aiData.predictions.confidence || 0.75,
              algorithm: aiData.predictions.algorithm || 'Linear Regression'
            };
            setPredictions(nextPredictions);
          }

          const normalizedInsights = ensureBaselineInsights([
            ...processedInsights,
            ...buildEmbeddedOptimizationInsight(subscriptionData)
          ], {
            provider: 'aws',
            aiPredictionEnabled,
            anomalyDetectionEnabled,
            rightsizingEnabled,
            hasPredictions: Boolean(nextPredictions)
          });
          setInsights(normalizedInsights);
          setAnalysisStatus(nextPredictions ? 'ready' : 'unavailable');

          awsInsightsCacheRef.current[requestKey] = {
            insights: normalizedInsights,
            predictions: nextPredictions
          };
        } catch (apiError) {
          console.error('❌ Erro na API de IA AWS:', apiError);
          setPredictions(null);
          setAnalysisStatus('unavailable');
          setInsights([{
            type: 'warning',
            title: 'IA aguardando histórico real',
            description: 'Ainda não há histórico positivo suficiente da AWS para gerar previsão confiável.',
            priority: 'medium'
          }]);
        }
        
      } else if (!isAzure) {
        const embeddedAI = extractEmbeddedAIData(subscriptionData);
        const normalizedInsights = ensureBaselineInsights(embeddedAI.insights, {
          provider,
          aiPredictionEnabled,
          anomalyDetectionEnabled,
          rightsizingEnabled,
          hasPredictions: Boolean(embeddedAI.predictions)
        });

        setPredictions(embeddedAI.predictions);
        setInsights(normalizedInsights);
        setAnalysisStatus(embeddedAI.predictions || embeddedAI.insights.length ? 'ready' : 'unavailable');
        setHistoricalLoading(false);
      } else {
        // Lógica para Azure (código original)
        let subscriptionId = localStorage.getItem('selectedSubscription');
        
        // Se não tem subscription salva, usar a do subscriptionData
        if (!subscriptionId && subscriptionData) {
          // Tentar diferentes caminhos onde o ID pode estar
          subscriptionId = subscriptionData.subscription?.id || 
                          subscriptionData.subscriptionId ||
                          subscriptionData.overview?.subscriptionId;
          
          if (subscriptionId) {
            localStorage.setItem('selectedSubscription', subscriptionId);
          }
        }
        
        if (!subscriptionId) {
          console.error('❌ Dados disponíveis:', {
            hasSubscriptionData: !!subscriptionData,
            subscriptionKeys: subscriptionData ? Object.keys(subscriptionData) : [],
            subscriptionObj: subscriptionData?.subscription
          });
          throw new Error('Subscription não encontrada');
        }
        
        // Chamar diretamente a API de insights (o backend gerencia o token)
        const response = await apiClient.get(`/ai/insights/${subscriptionId}`);
        const aiData = response.data;
        
        // Processar insights da IA
        const processedInsights = aiData.insights?.map((insight: any) => ({
          type: insight.type === 'prediction' ? 'info' : 
                insight.type === 'anomaly' ? 'warning' : 
                insight.type === 'optimization' ? 'success' : 'info',
          title: insight.title,
          description: insight.description,
          priority: insight.priority || 'medium',
          details: insight.details || insight.data || []
        })) || [];
        
        // Processar predições
        let nextPredictions: PredictionData | null = null;
        if (aiData.predictions) {
          nextPredictions = {
            predictions: aiData.predictions.predictions || [],
            trend: aiData.predictions.trend || 'increasing',
            confidence: aiData.predictions.confidence || 0.7,
            algorithm: aiData.predictions.algorithm || 'IA'
          };
          setPredictions(nextPredictions);
          setAnalysisStatus('ready');
        } else {
          setPredictions(null);
          setAnalysisStatus('unavailable');
        }

        setInsights(ensureBaselineInsights([
          ...processedInsights,
          ...buildEmbeddedOptimizationInsight(subscriptionData)
        ], {
          provider: subscriptionData?.provider || 'azure',
          aiPredictionEnabled,
          anomalyDetectionEnabled,
          rightsizingEnabled,
          hasPredictions: Boolean(nextPredictions)
        }));

        setHistoricalLoading(false);
      }
      
    } catch (error) {
      console.error('❌ Erro ao carregar insights de IA:', error);
      setPredictions(null);
      setAnalysisStatus('unavailable');
      setInsights([{
        type: 'warning',
        title: 'Insights indisponíveis',
        description: 'Não foi possível obter dados reais de IA neste momento.',
        priority: 'medium'
      }]);
    } finally {
      setLoading(false);
    }
  }, [requestKey, subscriptionData, aiPredictionEnabled, anomalyDetectionEnabled, rightsizingEnabled]);

  useEffect(() => {
    if (!subscriptionData || !requestKey) {
      return;
    }

    const now = Date.now();
    const previousRequest = lastRequestRef.current;
    if (previousRequest && previousRequest.key === requestKey && now - previousRequest.timestamp < 30000) {
      return;
    }

    lastRequestRef.current = {
      key: requestKey,
      timestamp: now
    };

    loadAIInsights();
    loadLearningStatus();
  }, [subscriptionData, requestKey, loadAIInsights, loadLearningStatus]);

  if (!aiPredictionEnabled && !anomalyDetectionEnabled && !rightsizingEnabled) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <BulbOutlined style={{ fontSize: '32px', color: '#1890ff', marginBottom: '12px' }} />
          <p>Ative os recursos de IA na seção Premium para ver insights inteligentes</p>
        </div>
      </Card>
    );
  }

  const chartContext: ChartContext | null = predictions && subscriptionData ? (() => {
    const isAWS = subscriptionData?.provider === 'aws';
    const isGCP = subscriptionData?.provider === 'gcp';
    
    // Usar dados históricos reais
    let historicalData;
    let currentCost;
    let labels;
    let currency = isAWS || isGCP ? '$' : 'R$';
    const excludedPartialMonth = subscriptionData?.overview?.monthlyAverageCalculation?.excludedPartialMonth;
    
    if (isAWS) {
      const temporalCosts = subscriptionData.temporalCosts || {};
      const monthLabels = Array.isArray(temporalCosts.months) ? temporalCosts.months : [];
      const seriesKeys = Object.keys(temporalCosts).filter(key => key !== 'months');

      if (seriesKeys.length > 0 && monthLabels.length > 0) {
        historicalData = monthLabels.map((_: string, index: number) =>
          seriesKeys.reduce((sum: number, key: string) => sum + (Number(temporalCosts[key]?.[index]) || 0), 0)
        );
        labels = monthLabels;
      } else {
        currentCost = subscriptionData.overview?.monthlyAverageCost || 0;
        historicalData = currentCost > 0 ? [currentCost] : [];
        labels = historicalData.length > 0 ? [subscriptionData.period || new Date().toLocaleDateString('pt-BR', { month: 'short' })] : [];
      }

      currentCost = historicalData[historicalData.length - 1] || subscriptionData.overview?.monthlyAverageCost || 0;
    } else {
      const temporalCosts = subscriptionData.temporalCosts || {};
      const monthLabels = Array.isArray(temporalCosts.months) ? temporalCosts.months : [];
      const seriesKeys = Object.keys(temporalCosts).filter(key => key !== 'months');

      if (seriesKeys.length > 0 && monthLabels.length > 0) {
        historicalData = monthLabels.map((_: string, index: number) =>
          seriesKeys.reduce((sum: number, key: string) => sum + (Number(temporalCosts[key]?.[index]) || 0), 0)
        );
        labels = monthLabels;
      } else {
        currentCost = subscriptionData.overview?.monthlyAverageCost || 0;
        historicalData = currentCost > 0 ? [currentCost] : [];
        labels = historicalData.length > 0 ? [subscriptionData.period || new Date().toLocaleDateString('pt-BR', { month: 'short' })] : [];
      }

      currentCost = historicalData[historicalData.length - 1] || subscriptionData.overview?.monthlyAverageCost || 0;
    }

    // O custo do mês corrente é parcial e não pode aparecer como um fechamento
    // nem servir como ponto de partida da projeção.
    if (excludedPartialMonth && historicalData.length > 1 && labels.length === historicalData.length) {
      historicalData = historicalData.slice(0, -1);
      labels = labels.slice(0, -1);
      currentCost = historicalData[historicalData.length - 1] || subscriptionData.overview?.monthlyAverageCost || 0;
    }
    
    // Com mês parcial excluído, a primeira previsão representa o mês corrente.
    const predictionLabels = Array.from({ length: predictions.predictions.length }, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() + i + (excludedPartialMonth ? 0 : 1));
      return date.toLocaleDateString('pt-BR', { month: 'short' });
    });
    
    const positiveHistoricalPoints = historicalData.filter((value: number) => value > 0).length;
    const hasPredictedValues = predictions.predictions.some(value => Number(value) > 0);

    if (positiveHistoricalPoints < 3 || !hasPredictedValues) {
      return null;
    }

    const allLabels = [...labels, ...predictionLabels];
    
    return {
      data: {
        labels: allLabels,
        datasets: [
          {
            label: `Custos Históricos (${currency})`,
            data: [...historicalData, ...Array(predictionLabels.length).fill(null)],
            borderColor: '#52c41a',
            backgroundColor: 'rgba(82, 196, 26, 0.1)',
            fill: false,
            tension: 0.3
          },
          {
            label: `Previsão da IA - ${predictions.algorithm || 'IA'} (${currency})`,
            data: [...Array(historicalData.length - 1).fill(null), currentCost, ...predictions.predictions],
            borderColor: '#1890ff',
            backgroundColor: 'rgba(24, 144, 255, 0.1)',
            borderDash: [5, 5],
            fill: false,
            tension: 0.3
          }
        ]
      },
      historicalPoints: historicalData.length,
      positiveHistoricalPoints,
      hasPredictedValues
    };
  })() : null;

  const trendDisplay = predictions ? (() => {
    if (predictions.trend === 'increasing') {
      return {
        label: 'Crescimento',
        color: '#ff4d4f',
        icon: <RiseOutlined style={{ color: '#ff4d4f' }} />
      };
    }

    if (predictions.trend === 'decreasing') {
      return {
        label: 'Redução',
        color: '#52c41a',
        icon: <RiseOutlined style={{ color: '#52c41a', transform: 'rotate(180deg)' }} />
      };
    }

    return {
      label: 'Estável',
      color: '#1890ff',
      icon: <RiseOutlined style={{ color: '#1890ff', transform: 'rotate(90deg)' }} />
    };
  })() : null;

  const learningStats = learningStatus?.learningStats;
  const modelInfo = learningStatus?.modelInfo;
  const pendingStats = learningStatus?.pendingStats;
  const pendingPredictions = Number(pendingStats?.pending_predictions || 0);
  const nextValidationLabel = pendingStats?.next_validation_at
    ? new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(new Date(`${pendingStats.next_validation_at}T12:00:00Z`))
    : null;
  const currentAccuracy = Number(learningStats?.current_accuracy || 0);
  const hasLearningHistory = Number(learningStats?.total_predictions || 0) > 0;
  const accuracyPercent = hasLearningHistory ? Math.round(Math.max(currentAccuracy, 0) * 100) : 0;
  const totalDataPoints = Math.max(
    Number(modelInfo?.total_data_points || 0),
    Number(chartContext?.positiveHistoricalPoints || 0)
  );
  const modelLabel = modelInfo?.current_model === 'personalized'
    ? 'Personalizado'
    : hasLearningHistory ? 'Global validado' : 'Global sem validação';
  const accuracyLabel = hasLearningHistory ? 'Acurácia validada' : 'Sem validação';
  const learningStatusLabel = hasLearningHistory
    ? accuracyPercent >= 80 ? 'Aprendizado estavel' : 'Acompanhando acuracia'
    : 'Aguardando validacoes';

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <RobotOutlined style={{ fontSize: '20px', color: '#1890ff', marginRight: '8px' }} />
        <h3 style={{ margin: 0 }}>Insights com Inteligência Artificial</h3>
        <Badge
          count={analysisStatus === 'loading' ? 'Analisando' : analysisStatus === 'ready' ? 'IA ativa' : analysisStatus === 'unavailable' ? 'Histórico insuficiente' : 'IA'}
          style={{
            backgroundColor: analysisStatus === 'ready' ? '#52c41a' : analysisStatus === 'unavailable' ? '#faad14' : '#1890ff',
            marginLeft: '8px'
          }}
        />
      </div>

      {loading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <p style={{ marginTop: '16px' }}>Analisando dados com IA...</p>
          </div>
        </Card>
      ) : (
        <>
          <Card
            size="small"
            style={{
              marginBottom: '16px',
              background: isDark ? '#161b22' : '#f8fbff',
              borderColor: isDark ? '#30363d' : '#d6e8ff'
            }}
          >
            <Row gutter={[16, 12]}>
              <Col xs={24} md={12}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <ThunderboltOutlined style={{ color: '#1677ff', fontSize: 18, marginTop: 3 }} />
                  <div>
                    <div style={{ fontWeight: 600, color: isDark ? '#f0f6fc' : '#1f2937' }}>
                      Analise executada durante a consulta
                    </div>
                    <div style={{ color: isDark ? '#b7c1cc' : '#5b6472', fontSize: 13, lineHeight: 1.5 }}>
                      Ao abrir ou atualizar o dashboard, a IA analisa os dados disponiveis naquele momento para gerar previsoes,
                      anomalias, insights e recomendacoes.
                    </div>
                  </div>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <HistoryOutlined style={{ color: '#52c41a', fontSize: 18, marginTop: 3 }} />
                  <div>
                    <div style={{ fontWeight: 600, color: isDark ? '#f0f6fc' : '#1f2937' }}>
                      Validação automática programada
                    </div>
                    <div style={{ color: isDark ? '#b7c1cc' : '#5b6472', fontSize: 13, lineHeight: 1.5 }}>
                      O job diário procura previsões cujo custo real já tenha fechado. Acurácia e aprendizado só são
                      apresentados quando essa comparação tiver sido persistida.
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          {canSeeAiLearningDiagnostics && (
          <Card
            size="small"
            title="Aprendizado da IA"
            style={{
              marginBottom: '16px',
              background: isDark ? '#1f1f1f' : '#fff',
              borderColor: isDark ? '#434343' : '#d9d9d9'
            }}
          >
            {learningLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Spin size="small" />
                <span style={{ color: isDark ? '#bfbfbf' : '#666' }}>Carregando metricas de aprendizado...</span>
              </div>
            ) : (
              <div className="ai-learning">
                <div className="ai-learning__score">
                  <Progress
                    type="dashboard"
                    percent={accuracyPercent}
                    width={104}
                    strokeColor={accuracyPercent >= 80 ? '#52c41a' : '#faad14'}
                    format={(percent) => hasLearningHistory ? `${percent}%` : 'N/D'}
                  />
                  <div style={{ textAlign: 'center', marginTop: 8, color: isDark ? '#bfbfbf' : '#666', fontSize: 12 }}>
                    {accuracyLabel}
                  </div>
                </div>
                <div className="ai-learning__content">
                  <Row gutter={[16, 12]}>
                    <Col xs={24} sm={12} xl={6}>
                      <Statistic
                        className="ai-learning__stat"
                        title="Modelo em uso"
                        value={modelLabel}
                        valueStyle={{ fontSize: 16, lineHeight: 1.3 }}
                      />
                    </Col>
                    <Col xs={24} sm={12} xl={6}>
                      <Statistic
                        className="ai-learning__stat"
                        title="Previsões validadas"
                        value={hasLearningHistory ? learningStats?.total_predictions || 0 : pendingPredictions}
                        suffix={!hasLearningHistory && pendingPredictions === 1 ? 'pendente' : !hasLearningHistory && pendingPredictions > 1 ? 'pendentes' : undefined}
                        valueStyle={{ fontSize: 16, lineHeight: 1.3 }}
                      />
                    </Col>
                    <Col xs={24} sm={12} xl={6}>
                      <Statistic
                        className="ai-learning__stat"
                        title="Pontos históricos analisados"
                        value={totalDataPoints > 0 ? totalDataPoints : 'Coletando'}
                        valueStyle={{ fontSize: 16, lineHeight: 1.3 }}
                      />
                    </Col>
                    <Col xs={24} sm={12} xl={6}>
                      <Statistic
                        className="ai-learning__stat"
                        title="Status"
                        value={hasLearningHistory
                          ? learningStatusLabel
                          : nextValidationLabel ? `Validação em ${nextValidationLabel}` : 'Coletando baseline'}
                        valueStyle={{ fontSize: 16, lineHeight: 1.3 }}
                      />
                    </Col>
                  </Row>
                  <div
                    className="ai-learning__explanation"
                    style={{ color: isDark ? '#bfbfbf' : '#666' }}
                  >
                    {hasLearningHistory
                      ? `O percentual acima é a acurácia validada contra custos reais fechados. Melhoria acumulada: ${learningStats?.improvement || 0}%.`
                      : pendingPredictions > 0
                        ? `${pendingPredictions} previsão(ões) aguardando o fechamento do custo real${nextValidationLabel ? `; primeira validação prevista para ${nextValidationLabel}` : ''}. A acurácia será exibida somente após essa comparação.`
                        : 'A primeira previsão ainda está sendo preparada. A acurácia será exibida somente depois que um mês previsto fechar e o custo real puder ser comparado.'}
                    {hasLearningHistory && modelInfo?.recommendation && (
                      <div style={{ marginTop: 4 }}>{modelInfo.recommendation}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Card>
          )}

          {/* Insights Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {insights.map((insight, index) => {
              const isExpanded = expandedCard === index;
              const toggleExpand = () => {
                setExpandedCard(isExpanded ? null : index);
              };
              
              return (
                <Col 
                  xs={24} 
                  lg={isExpanded ? 24 : 8} 
                  key={index}
                  style={{
                    transition: 'all 0.3s ease',
                    display: expandedCard !== null && !isExpanded ? 'none' : 'block'
                  }}
                >
                  <Card 
                    style={{ 
                      height: '100%',
                      background: isDark ? '#1f1f1f' : '#fff',
                      borderColor: isDark ? '#434343' : '#d9d9d9'
                    }}
                  >
                    <div style={{ marginBottom: '16px' }}>
                      <Alert
                        message={insight.title}
                        description={insight.description}
                        type={insight.type}
                        showIcon
                        style={{ 
                          border: 'none', 
                          padding: 0, 
                          background: 'transparent',
                          color: isDark ? '#fff' : undefined
                        }}
                      />
                    </div>
                    
                    <div 
                      onClick={toggleExpand}
                      style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        padding: '10px',
                        cursor: 'pointer',
                        borderRadius: '6px',
                        background: isDark ? '#262626' : '#f5f5f5',
                        border: isDark ? '1px solid #434343' : '1px solid #e8e8e8',
                        transition: 'all 0.3s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = isDark ? '#303030' : '#e8e8e8';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = isDark ? '#262626' : '#f5f5f5';
                      }}
                    >
                      <span style={{ 
                        fontSize: '12px', 
                        color: isDark ? '#bfbfbf' : '#666',
                        marginRight: '8px'
                      }}>
                        {isExpanded ? 'Ocultar detalhes' : 'Ver detalhes'}
                      </span>
                      <DownOutlined 
                        style={{ 
                          fontSize: '10px',
                          color: isDark ? '#bfbfbf' : '#666',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s'
                        }} 
                      />
                    </div>
                    
                    {isExpanded && insight.details && insight.details.length > 0 && (
                      <div style={{ 
                        marginTop: '16px', 
                        padding: '16px', 
                        background: isDark ? '#141414' : '#fafafa', 
                        borderRadius: '6px',
                        border: isDark ? '1px solid #303030' : '1px solid #f0f0f0'
                      }}>
                        {Array.isArray(insight.details) ? (() => {
                          const rows = insight.details
                            .filter(item => item && typeof item === 'object')
                            .map((item, idx) => ({ ...item, key: idx }));
                          const keys = Array.from(new Set(
                            rows.flatMap(row =>
                              Object.keys(row).filter(key => {
                                if (key === 'key') return false;
                                const value = row[key];
                                return value !== undefined && value !== null && value !== '';
                              })
                            )
                          ));
                          const longTextKeys = [
                            'recommendation',
                            'recomendacao',
                            'motivo',
                            'evidencia',
                            'justificativa',
                            'origem',
                            'proximaAcao',
                            'explicacao',
                            'item'
                          ];
                          const columnWidthByKey: Record<string, number> = {
                            recommendation: 300,
                            recomendacao: 300,
                            motivo: 280,
                            evidencia: 280,
                            justificativa: 280,
                            origem: 240,
                            proximaAcao: 360,
                            explicacao: 320,
                            periodo: 140,
                            valorObservado: 160,
                            baselineEsperado: 170,
                            impacto: 260,
                            tipo: 170,
                            item: 220,
                            periodoAnterior: 170,
                            periodoAtual: 170,
                            principalDriver: 240,
                            acao: 160,
                            risco: 120,
                            esforco: 120,
                            confianca: 130,
                            economia: 150,
                            impact: 150,
                            impactoMensal: 160
                          };

                          return rows.length && keys.length ? (
                          <Table
                            size="small"
                            dataSource={rows}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            tableLayout="auto"
                            columns={keys.map(key => {
                              const columnTitles: { [key: string]: string } = {
                                'algorithm': 'Algoritmo',
                                'confidence': 'Confiança',
                                'trend': 'Tendência',
                                'nextMonthPrediction': 'Próximo Mês',
                                'resource': 'Recurso',
                                'type': 'Tipo',
                                'currentCost': 'Custo Atual',
                                'status': 'Status',
                                'potentialSaving': 'Economia Potencial',
                                'metric': 'Métrica',
                                'value': 'Valor',
                                'monthlyCost': 'Custo Mensal',
                                'percentageOfTotal': '% do Total',
                                'recommendation': 'Recomendação',
                                'pattern': 'Padrão',
                                'period': 'Período',
                                'impact': 'Impacto',
                                'metrica': 'Métrica',
                                'valor': 'Valor',
                                'percentual': 'Percentual',
                                'confianca': 'Confiança',
                                'driver': 'Driver',
                                'variacao': 'Variação',
                                'contribuicao': 'Contribuição',
                                'motivo': 'Motivo',
                                'acao': 'Ação',
                                'recomendacao': 'Recomendação',
                                'score': 'Score',
                                'prioridade': 'Prioridade',
                                'economia': 'Economia',
                                'risco': 'Risco',
                                'esforco': 'Esforço',
                                'evidencia': 'Evidência',
                                'justificativa': 'Justificativa',
                                'origem': 'Origem',
                                'proximaAcao': 'Próxima Ação',
                                'periodo': 'Período',
                                'valorObservado': 'Valor Observado',
                                'baselineEsperado': 'Baseline Esperado',
                                'impacto': 'Impacto',
                                'tipo': 'Tipo',
                                'principalDriver': 'Principal Driver',
                                'explicacao': 'Explicação',
                                'item': 'Item',
                                'periodoAnterior': 'Período Anterior',
                                'periodoAtual': 'Período Atual'
                              };
                              
                              return {
                                title: columnTitles[key] || key.charAt(0).toUpperCase() + key.slice(1),
                                dataIndex: key,
                                key: key,
                                width: columnWidthByKey[key] || 140,
                                render: (text: any) => {
                                  if (text === undefined || text === null || text === '') {
                                    return '-';
                                  }
                                  if (typeof text === 'number' && (key.toLowerCase().includes('cost') || key.toLowerCase().includes('economia'))) {
                                    return `R$ ${text.toFixed(2)}`;
                                  }
                                  if (longTextKeys.includes(key) && typeof text === 'string') {
                                    return (
                                      <div style={{ 
                                        width: key === 'proximaAcao' ? '340px' : '260px',
                                        whiteSpace: 'pre-wrap',
                                        overflowWrap: 'anywhere',
                                        lineHeight: '1.45'
                                      }}>
                                        {text}
                                      </div>
                                    );
                                  }
                                  return String(text);
                                }
                              };
                            })}
                          />
                          ) : (
                            <div style={{ color: isDark ? '#bfbfbf' : '#666' }}>
                              Sem detalhes estruturados disponíveis para este insight.
                            </div>
                          );
                        })() : (
                          <div style={{ color: isDark ? '#fff' : '#000' }}>
                            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                              {JSON.stringify(insight.details, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                </Col>
              );
            })}
          </Row>

          {/* Prediction Chart */}
          {aiPredictionEnabled && predictions && chartContext && (
            expandedCard === null ||
            /predi|previs|forecast/i.test(insights[expandedCard]?.title || '')
          ) && (
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Projeção de Custos Gerada pela IA
                  {historicalLoading && (
                    <span style={{ fontSize: '12px', color: '#1890ff' }}>
                      (Carregando dados históricos...)
                    </span>
                  )}
                </div>
              } 
              style={{ 
                marginBottom: '16px',
                background: isDark ? '#1f1f1f' : '#fff',
                borderColor: isDark ? '#434343' : '#d9d9d9'
              }}
            >
              <Row gutter={16}>
                <Col span={18}>
                  <div style={{ height: '300px' }}>
                    <Line 
                      data={chartContext.data} 
                      options={{ 
                        responsive: true, 
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: 'top' }
                        },
                        scales: {
                          y: {
                            beginAtZero: false,
                            ticks: {
                              callback: function(value) {
                                return 'R$ ' + value;
                              }
                            }
                          }
                        }
                      }} 
                    />
                  </div>
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Tendência"
                    value={trendDisplay?.label || 'Estável'}
                    prefix={trendDisplay?.icon}
                    valueStyle={{ 
                      color: trendDisplay?.color || '#1890ff',
                      fontSize: '16px'
                    }}
                  />
                  <Statistic
                    title="Confiança da IA"
                    value={predictions.confidence * 100}
                    suffix="%"
                    valueStyle={{ fontSize: '16px' }}
                    style={{ marginTop: '16px' }}
                  />
                  <div style={{ marginTop: 16, fontSize: 12, color: isDark ? '#bfbfbf' : '#666' }}>
                    Base: {chartContext.positiveHistoricalPoints} meses com custo real positivo.
                  </div>
                </Col>
              </Row>
            </Card>
          )}

          {aiPredictionEnabled && predictions && !chartContext && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Projeção de custos indisponível"
              description="A IA foi chamada, mas o histórico real ainda não tem pelo menos 3 meses com custo positivo. O gráfico fica oculto para evitar uma previsão artificial."
            />
          )}

          {/* Anomaly Detection */}
          {anomalyDetectionEnabled && (() => {
            const anomalyInsight = insights.find(i => i.title.includes('Anomalias') || i.title.includes('Detectadas'));
            
            // Usar a mesma fonte de dados que o insight
            const anomalyMatch = anomalyInsight?.description?.match(/\d+/);
            const anomalyCount = anomalyMatch ? parseInt(anomalyMatch[0]) : 0;

            return (
              <Card 
                size="small" 
                style={{ 
                  marginBottom: '16px',
                  background: isDark ? '#1f1f1f' : '#fff',
                  borderColor: isDark ? '#434343' : '#d9d9d9'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <AlertOutlined style={{ color: anomalyCount > 0 ? '#ff4d4f' : '#52c41a', marginRight: '8px' }} />
                  <span>Detecção de Anomalias: </span>
                  <Badge 
                    count={`${anomalyCount} anomalias`} 
                    style={{ 
                      backgroundColor: anomalyCount > 0 ? '#ff4d4f' : '#52c41a', 
                      marginLeft: '8px' 
                    }} 
                  />
                  <span style={{ marginLeft: '12px', fontSize: '12px', color: '#666' }}>
                    Última verificação: agora
                  </span>
                </div>
              </Card>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default AIInsights;
